import { IPG_ErrorCode } from '@lib/types';
import { ErrorCode, IProcessPaymentTransaction, IProcessPaymentWithManifestRequest } from '@ace-clublabs/payment-types';
import axios from 'axios';
import StatusCode from 'status-code-enum';
import { ProcessPayment } from '../contracts';
import { ErrorSeverity } from '../../shared/types';
import { LogCannon } from '@ace-clublabs/log-cannon';
import { createAxiosConfig } from '../mockHelper';
import CookieHelper from '../../utils/cookieHelper';

export const ProcessPaymentApi =
  (endpoint: string): ProcessPayment =>
    async (request: IProcessPaymentWithManifestRequest) => {
      try {
        const { manifestId, paymentType, storePayment, clientToken } = request;
        const logInfo = { endpoint, manifestId, paymentType, storePayment, clientToken };
        LogCannon.blast('ProcessPaymentApi start', logInfo);
        const scenario = CookieHelper.getCookie('scenario');
        const config = createAxiosConfig(scenario, endpoint);
        const response = await axios.post(endpoint, request, config);

        if (!response?.data?.transactionResults && response?.data?.storePaymentError) {
          return Promise.reject({
            code: ErrorCode.StorePaymentError,
            type: ErrorSeverity.SOFT,
          });
        }
        LogCannon.blast('ProcessPaymentApi success');
        return Promise.resolve(response.data);
      } catch (error: any) {
        LogCannon.error('ProcessPaymentApi error', { error: error?.message || error });
        if (error?.message === 'Network Error') {
          return Promise.reject({
            code: ErrorCode.NetworkError,
            type: ErrorSeverity.SOFT,
          });
        }
        if (error.response) {

          let code;

          if (error.response.status === StatusCode.ServerErrorGatewayTimeout) {
            return Promise.reject({
              code: ErrorCode.NetworkTimeout,
              type: ErrorSeverity.SOFT,
            });
          } else if (error.response.status === StatusCode.ClientErrorPaymentRequired) {
            let paymentSoftErrors = [
              ErrorCode.TryAgain,
              ErrorCode.InvalidCard,
              ErrorCode.InvalidCVV,
              ErrorCode.InvalidToken,
              ErrorCode.InvalidExpirationDate,
              ErrorCode.InvalidZipCode,
              ErrorCode.CardTypeNotAllowed,
              ErrorCode.InvalidInsuranceAutopayPaymentMethod,
              ErrorCode.ChargeDeclined,
              ErrorCode.InvalidAccountHolderName,
              ErrorCode.InvalidAccountNumber,
              ErrorCode.InvalidRoutingNumber,
              ErrorCode.InvalidPaymentGatewayToken,
              ErrorCode.StandaloneAutoPayError,
              ErrorCode.UnknownCardType,
              ErrorCode.StorePaymentError,
              ErrorCode.InsufficientFunds,
              ErrorCode.AutopayNotAttempted,
            ];

            let paymentHardErrors = [
              ErrorCode.InvalidAmount,
              ErrorCode.SystemError,
            ];

            const transactionResults = error?.response?.data?.transactionResults || [];
            transactionResults.forEach((transaction: IProcessPaymentTransaction) => {
              code = transaction.paymentError?.code || transaction.autoPayError?.code;
            });

            if (!code && error?.response?.data?.storePaymentError) {
              code = error?.response?.data?.storePaymentError?.code;
            }

            if (!code && error?.response?.data?.error?.code) {
              code = error?.response?.data?.error?.code;
            }

            if (code && paymentSoftErrors.includes(code)) {
              return Promise.reject({
                code,
                type: ErrorSeverity.SOFT,
              });
            }
            if (code && paymentHardErrors.includes(code)) {
              return Promise.reject({
                code,
                type: ErrorSeverity.HARD,
              });
            }

            return Promise.resolve(error.response.data);
          } else if (error.response.status === StatusCode.ClientErrorTooManyRequests) {
            return Promise.reject({
              code: ErrorCode.TooManyRequests,
              type: ErrorSeverity.HARD,
            });
          } else if (error.response.status === StatusCode.ClientErrorBadRequest) {
            return Promise.reject({
              code: error.response.data?.error?.code,
              type: ErrorSeverity.SOFT,
            });
          }
          if (error.response.status === StatusCode.ServerErrorBadGateway) {
            return Promise.reject({
              code: 'gateway_timeout',
              type: ErrorSeverity.HARD,
            });
          } else {
            code = error.response.data?.error?.code;

            let gatewaySoftErrors = [
              ErrorCode.GatewayTokenFailedToWrite,
              ErrorCode.InvalidAccountHolderName,
              IPG_ErrorCode.Network,
              IPG_ErrorCode.UnexpectedResponse,
              IPG_ErrorCode.Unknown,
              IPG_ErrorCode.MissingCVV,
              IPG_ErrorCode.Rejected,
              IPG_ErrorCode.SessionConsumed,
              IPG_ErrorCode.SessionInsert,
              IPG_ErrorCode.SessionInvalid,
            ];

            const type = gatewaySoftErrors.includes(code) ? ErrorSeverity.SOFT : ErrorSeverity.HARD;

            return Promise.reject({
              code,
              type,
            });
          }
        } else {
          return Promise.reject({
            code: ErrorCode.UnhandledError,
            type: ErrorSeverity.HARD,
          });
        }
      }
    };
